import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, Mail, AlertTriangle, Monitor } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface SessionInfo {
  ip_address: string;
  last_activity: string;
}

interface LoginPageProps {
  onLogin: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    sessionInfo?: SessionInfo;
  }>;
  onForceLogin: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
}

export function LoginPage({ onLogin, onForceLogin }: LoginPageProps) {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeSession, setActiveSession] = useState<SessionInfo | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setActiveSession(null);

    const result = await onLogin(email, password);

    if (!result.success) {
      if (result.error === 'active_session' && result.sessionInfo) {
        setActiveSession(result.sessionInfo);
      } else {
        setError(result.error || t('login.error'));
      }
    }

    setIsLoading(false);
  };

  const handleForceLogin = async () => {
    setIsLoading(true);
    setError("");

    const result = await onForceLogin(email, password);

    if (!result.success) {
      setError(result.error || t('login.error'));
      setActiveSession(null);
    }

    setIsLoading(false);
  };

  const formatLastActivity = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return t('activeSession.justNow');
    if (diffMins < 60) return t('activeSession.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('activeSession.hoursAgo', { count: diffHours });
    return date.toLocaleString('pl-PL');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
            <Lock className="h-7 w-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">{t('login.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('login.subtitle')}
          </p>
        </div>

        {activeSession && (
          <Alert variant="destructive" className="border-orange-500 bg-orange-500/10">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <AlertTitle className="text-orange-500">{t('activeSession.title')}</AlertTitle>
            <AlertDescription className="text-muted-foreground space-y-3">
              <p>{t('activeSession.description')}</p>
              <div className="flex items-center gap-2 text-sm bg-background/50 rounded p-2">
                <Monitor className="h-4 w-4" />
                <span>IP: {activeSession.ip_address}</span>
              </div>
              <p className="text-xs">
                {t('activeSession.lastActivity')} {formatLastActivity(activeSession.last_activity)}
              </p>
              <Button 
                onClick={handleForceLogin} 
                variant="outline" 
                className="w-full mt-2 border-orange-500 text-orange-500 hover:bg-orange-500/10"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {t('activeSession.forceLogin')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('login.emailLabel')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                  setActiveSession(null);
                }}
                placeholder={t('login.emailPlaceholder')}
                className="pl-10"
                autoFocus
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">{t('login.passwordLabel')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                placeholder={t('login.passwordPlaceholder')}
                className="pl-10"
                disabled={isLoading}
                required
                minLength={1}
              />
            </div>
          </div>

          {error && !activeSession && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <Button 
            type="submit" 
            className="w-full bg-gradient-primary" 
            disabled={isLoading || !email || !password}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('login.loading')}
              </>
            ) : (
              t('login.submit')
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          {t('login.footer')}
        </p>
      </div>
    </div>
  );
}
